/**
 * Servicio para Utilidades de Trabajadores Cesados
 * Alineado con: utilidades_endpoints_reference.json (2026-03-19)
 *
 * Endpoints implementados:
 * - GET  /solicitudes
 * - PATCH /solicitudes/{id}/fecha-abono
 * - DELETE /solicitudes/{id}
 * - POST /cargar-montos
 * - POST /certificados/subir-archivo       (Step 1)
 * - POST /certificados/validar-zip          (Step 2)
 * - GET  /certificados/migracion/{id}/resultados-validacion (Step 2b)
 * - POST /certificados/cargar               (Step 3)
 * - GET  /migracion/{id}/progreso
 * - GET  /migracion/{id}/resumen
 * - GET  /resumenes
 * - DELETE /migraciones/{id}
 * - GET  /solicitudes/{id}/certificado-url
 * - POST /sincronizar/bidireccional
 * - POST /sincronizar/firestore-to-postgres
 * - POST /sincronizar/postgres-to-firestore
 */

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, from, switchMap, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/app/environments/environment';
import { LoginFirebaseService } from 'src/app/features/auth/log-in/services/login-firebase.service';

import {
  ISolicitudesFilters,
  ISolicitudesListResponse,
  IResumenesFilters,
  IResumenesListResponse,
  ICargaMontosParams,
  ICargaMontosInitialResponse,
  ICargaMontosEstadoResponse,
  ICertificadosSubirArchivoRequest,
  ICertificadosSubirArchivoResponse,
  ICertificadosValidarZipResponse,
  IResultadosValidacionResponse,
  ICertificadosCargarRequest,
  ICertificadosCargarResponse,
  IProgresoMigracionResponse,
  IResumenMigracionDetalleResponse,
  IEditarFechaAbonoRequest,
  IEditarFechaAbonoResponse,
  IEliminarSolicitudResponse,
  IEliminarMigracionResponse,
  ICertificadoUrlResponse,
  ISincronizarBidireccionalResponse,
  ISincronizarUnidireccionalResponse,
  IProgramarFechaAbonoMasivoRequest,
  IProgramarFechaAbonoMasivoResponse,
  IProgresoProgramacionMasivaResponse,
} from '../interfaces/utilities.interface';

@Injectable({
  providedIn: 'root'
})
export class UtilitiesService {

  private baseUrl = environment.apiUrl;
  //private baseUrl = 'https://conectados-ms-cts-843317363130.us-east4.run.app';
  private apiPrefix = '/api/v1/personas/utilidades';
    // Cachear el token y evitar el roundtrip en cada poll
  private cachedHeaders: HttpHeaders | null = null;

  constructor(
    private http: HttpClient,
    private loginFirebaseService: LoginFirebaseService
  ) {}

  /* ============================================
     HEADERS AUTH
  ============================================ */
  private withAuthHeaders(): Observable<HttpHeaders> {
    return from(this.loginFirebaseService.getNonprodToken()).pipe(
      map(token => {
        if (!token) {
          throw new Error('No hay token nonprod disponible');
        }
        return new HttpHeaders({
          'Authorization': `Bearer ${token}`,
          'X-Tenant': 'Peru'
        });
      })
    );
  }

  private get cleanBase(): string {
    return this.baseUrl.replace(/\/$/, '');
  }


  getMigrationStatus(migrationId: number): Observable<any> {
      if (this.cachedHeaders) {
          // Token ya resuelto — HTTP directo (rápido para polling cada 2s)
          return this.http.get<any>(
              `${this.baseUrl}/cargar-montos/${migrationId}/estado`,
              { headers: this.cachedHeaders }
          );
      }
      // Primera vez: resolver token, cachear, y hacer el call
      return this.withAuthHeaders().pipe(
          tap(headers => this.cachedHeaders = headers),
          switchMap(headers =>
              this.http.get<any>(
                  `${this.baseUrl}/cargar-montos/${migrationId}/estado`,
                  { headers }
              )
          )
      );
  }

  // ======================================================
  // 1. GET /solicitudes
  // ======================================================
  getSolicitudUtilidades(filters: ISolicitudesFilters): Observable<ISolicitudesListResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers => {
        let params = new HttpParams()
          .set('pagina', filters.pagina ?? 1)
          .set('registros_por_pagina', filters.registros_por_pagina ?? 10);

        if (filters.estado)               params = params.set('estado', filters.estado);
        if (filters.dni)                  params = params.set('dni', filters.dni);
        if (filters.nombre_completo)      params = params.set('nombre_completo', filters.nombre_completo);
        if (filters.codigo_trabajador)    params = params.set('codigo_trabajador', filters.codigo_trabajador);
        if (filters.vigencia_cobrar)      params = params.set('vigencia_cobrar', filters.vigencia_cobrar);
        if (filters.nombre_banco)         params = params.set('nombre_banco', filters.nombre_banco);
        if (filters.fecha_solicitud_desde) params = params.set('fecha_solicitud_desde', filters.fecha_solicitud_desde);
        if (filters.fecha_solicitud_hasta) params = params.set('fecha_solicitud_hasta', filters.fecha_solicitud_hasta);
        if (filters.fecha_abono_desde)    params = params.set('fecha_abono_desde', filters.fecha_abono_desde);
        if (filters.fecha_abono_hasta)    params = params.set('fecha_abono_hasta', filters.fecha_abono_hasta);

        return this.http.get<ISolicitudesListResponse>(
          `${this.cleanBase}/api/v1/personas/utilidades/solicitudes`,
          { headers, params }
        );
      }),
      catchError(error => {
        console.error('Error al obtener solicitudes:', error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 2. PATCH /solicitudes/{id}/fecha-abono
  // ======================================================
  editarFechaAbono(
    solicitudId: string,
    request: IEditarFechaAbonoRequest
  ): Observable<IEditarFechaAbonoResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.patch<IEditarFechaAbonoResponse>(
          `${this.cleanBase}${this.apiPrefix}/solicitudes/${solicitudId}/fecha-abono`,
          request,
          { headers }
        )
      ),
      catchError(error => {
        console.error(`Error actualizando fecha de abono para solicitud ${solicitudId}:`, error);
        return throwError(() => error);
      })
    );
  }
  // ======================================================
  // 2b. POST /solicitudes/programar-fecha-abono-masivo
  // ======================================================
  programarFechaAbonoMasivo(
    request: IProgramarFechaAbonoMasivoRequest
  ): Observable<IProgramarFechaAbonoMasivoResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.post<IProgramarFechaAbonoMasivoResponse>(
          `${this.cleanBase}${this.apiPrefix}/solicitudes/programar-fecha-abono-masivo`,
          request,
          { headers }
        )
      ),
      catchError(error => {
        console.error('Error iniciando programación masiva de fecha de abono:', error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 2c. GET /migracion/{migration_id}/progreso
  // ======================================================
  obtenerProgresoProgramacionMasiva(
    migrationId: number
  ): Observable<IProgresoProgramacionMasivaResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.get<IProgresoProgramacionMasivaResponse>(
          `${this.cleanBase}${this.apiPrefix}/migracion/${migrationId}/progreso`,
          { headers }
        )
      ),
      catchError(error => {
        console.error(`Error obteniendo progreso de migración ${migrationId}:`, error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 3. DELETE /solicitudes/{solicitud_id}
  // ======================================================
  deleteSolicitud(solicitudId: string): Observable<IEliminarSolicitudResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.delete<IEliminarSolicitudResponse>(
          `${this.cleanBase}${this.apiPrefix}/solicitudes/${solicitudId}`,
          { headers }
        )
      ),
      catchError(error => {
        console.error(`Error eliminando solicitud ${solicitudId}:`, error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 4a. POST /cargar-montos  (multipart/form-data → respuesta inmediata)
  // ======================================================
  cargarMontos(
    archivo: File,
    datosMontos: ICargaMontosParams
  ): Observable<ICargaMontosInitialResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers => {
        const formData = new FormData();
        formData.append('archivo', archivo);
        formData.append('año_gravable', String(datosMontos.año_gravable));
        formData.append('renta_anual', String(datosMontos.renta_anual));
        formData.append('participacion_distribuir', String(datosMontos.participacion_distribuir));
        formData.append('dias_laborados', String(datosMontos.dias_laborados));
        formData.append('remuneraciones_total', String(datosMontos.remuneraciones_total));
        formData.append('solo_cesados', String(datosMontos.solo_cesados ?? true));
        if (datosMontos.redistribucion_monto != null)
          formData.append('redistribucion_monto', String(datosMontos.redistribucion_monto));
        if (datosMontos.redistribucion_anio != null)
          formData.append('redistribucion_anio', String(datosMontos.redistribucion_anio));

        return this.http.post<ICargaMontosInitialResponse>(
          `${this.cleanBase}${this.apiPrefix}/cargar-montos`,
          formData,
          { headers }
        );
      }),
      catchError(error => {
        console.error('Error cargando montos:', error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 4b. GET /cargar-montos/{migration_id}/estado  (polling)
  // ======================================================
  getCargaMontosEstado(migrationId: number): Observable<ICargaMontosEstadoResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.get<ICargaMontosEstadoResponse>(
          `${this.cleanBase}${this.apiPrefix}/cargar-montos/${migrationId}/estado`,
          { headers }
        )
      ),
      catchError(error => {
        console.error(`Error consultando estado de migración ${migrationId}:`, error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 5a. POST /certificados/subir-archivo  (Step 1 — JSON)
  // ======================================================
  certificadosSubirArchivo(
    request: ICertificadosSubirArchivoRequest
  ): Observable<ICertificadosSubirArchivoResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.post<ICertificadosSubirArchivoResponse>(
          `${this.cleanBase}${this.apiPrefix}/certificados/subir-archivo`,
          request,
          { headers: headers.set('Content-Type', 'application/json') }
        )
      ),
      catchError(error => {
        console.error('Error creando migración de certificados:', error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 5b. PUT to upload_url  (Step 1b — upload ZIP to GCS)
  // ======================================================
  uploadZipToGCS(uploadUrl: string, file: File): Observable<any> {
    return this.http.put(uploadUrl, file, {
      headers: new HttpHeaders({ 'Content-Type': 'application/zip' }),
      reportProgress: true,
      observe: 'events'
    });
  }

  // ======================================================
  // 5c. POST /certificados/validar-zip  (Step 2)
  // ======================================================
  certificadosValidarZip(migrationId: number): Observable<ICertificadosValidarZipResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.post<ICertificadosValidarZipResponse>(
          `${this.cleanBase}${this.apiPrefix}/certificados/validar-zip`,
          { migration_id: migrationId },
          { headers: headers.set('Content-Type', 'application/json') }
        )
      ),
      catchError(error => {
        console.error('Error validando ZIP:', error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 5d. GET /certificados/migracion/{id}/resultados-validacion  (Step 2b)
  // ======================================================
  // ======================================================
  // 5d. GET /certificados/migracion/{id}/resultados-validacion
  // ======================================================
  certificadosResultadosValidacion(
    migrationId: number,
    firma: string,
    page: number = 1,
    pageSize: number = 50,
    soloInvalidos: boolean = false
  ): Observable<IResultadosValidacionResponse> {
    
    // 1. Validación defensiva: evita enviar la cadena "undefined" al backend
    if (!firma) {
      console.error('El parámetro "firma" es obligatorio y no puede estar vacío');
      return throwError(() => new Error('Firma no proporcionada'));
    }

    return this.withAuthHeaders().pipe(
      switchMap(headers => {
        const url = `${this.cleanBase}${this.apiPrefix}/certificados/migracion/${migrationId}/resultados-validacion`;

        // 2. Conversión EXPLÍCITA a string para evitar problemas de parseo en FastAPI
        const params = new HttpParams()
          .set('firma', firma)
          .set('page', page.toString())
          .set('page_size', pageSize.toString())
          .set('solo_invalidos', soloInvalidos ? 'true' : 'false');

        return this.http.get<IResultadosValidacionResponse>(url, { headers, params });
      }),
      catchError(error => {
        console.error('Error obteniendo resultados de validación:', error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 5e. POST /certificados/cargar
  // ======================================================
  certificadosCargar(request: ICertificadosCargarRequest): Observable<ICertificadosCargarResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers => {
        const url = `${this.cleanBase}${this.apiPrefix}/certificados/cargar`;
        
        // 3. Verificación de seguridad: asegurar que headers sea una instancia de HttpHeaders
        // Angular ya inyecta 'Content-Type: application/json' automáticamente al enviar un objeto.
        // No es necesario forzarlo, lo que evita el error "headers.set is not a function".
        const safeHeaders = headers instanceof HttpHeaders 
          ? headers 
          : new HttpHeaders(headers as any);

        return this.http.post<ICertificadosCargarResponse>(url, request, { headers: safeHeaders });
      }),
      catchError(error => {
        console.error('Error lanzando carga de certificados:', error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 6. GET /migracion/{id}/progreso
  // ======================================================
  obtenerProgresoMigracion(migracionId: number): Observable<IProgresoMigracionResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.get<IProgresoMigracionResponse>(
          `${this.cleanBase}${this.apiPrefix}/migracion/${migracionId}/progreso`,
          { headers }
        )
      ),
      catchError(error => {
        console.error(`Error obteniendo progreso de migración ${migracionId}:`, error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 7. GET /migracion/{id}/resumen
  // ======================================================
  obtenerResumenMigracion(migracionId: number): Observable<IResumenMigracionDetalleResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.get<IResumenMigracionDetalleResponse>(
          `${this.cleanBase}${this.apiPrefix}/migracion/${migracionId}/resumen`,
          { headers }
        )
      ),
      catchError(error => {
        console.error(`Error obteniendo resumen de migración ${migracionId}:`, error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 8. GET /resumenes
  // ======================================================
  listarResumenes(filters: IResumenesFilters): Observable<IResumenesListResponse> {

    // console.log({filters});
    

    return this.withAuthHeaders().pipe(
      switchMap(headers => {
        let params = new HttpParams()
          .set('pagina', filters.pagina ?? 1)
          .set('registros_por_pagina', filters.registros_por_pagina ?? 10);

        if (filters.año_gravable) {
          params = params.set('año_gravable', filters.año_gravable);
        }
        if (filters.estado) {
          params = params.set('estado', filters.estado);
        }
        if (filters.usuario_creacion) {
          params = params.set('usuario_creacion', filters.usuario_creacion);
        }
        if (filters.fecha_desde) {
          params = params.set('fecha_desde', filters.fecha_desde);
        }
        if (filters.fecha_hasta) {
          params = params.set('fecha_hasta', filters.fecha_hasta);
        }
        if (filters.estado_contrato) {
          params = params.set('estado_contrato', filters.estado_contrato);
        }

        return this.http.get<IResumenesListResponse>(
          `${this.cleanBase}${this.apiPrefix}/resumenes`,
          { headers, params }
        );
      }),
      catchError(error => {
        console.error('Error listando resúmenes:', error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 9. DELETE /migraciones/{migration_id}   (cascada)
  // ======================================================
  deleteMigracion(migracionId: number): Observable<IEliminarMigracionResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.delete<IEliminarMigracionResponse>(
          `${this.cleanBase}${this.apiPrefix}/migraciones/${migracionId}`,
          { headers }
        )
      ),
      catchError(error => {
        console.error(`Error eliminando migración ${migracionId}:`, error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 10. GET /solicitudes/{solicitud_id}/certificado-url
  // ======================================================
  obtenerCertificadoUrl(solicitudId: string): Observable<ICertificadoUrlResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.get<ICertificadoUrlResponse>(
          `${this.cleanBase}${this.apiPrefix}/solicitudes/${solicitudId}/certificado-url`,
          { headers }
        )
      ),
      catchError(error => {
        console.error(`Error obteniendo URL de certificado para solicitud ${solicitudId}:`, error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 10b. GET /solicitudes/exportar-excel  (exportar todos)
  // ======================================================
  exportarSolicitudesExcel(filters: {
    estado?: string | null;
    fecha_desde?: string | null;
    fecha_hasta?: string | null;
    grupo_pago?: string | null;
    año_gravable?: number | null;
  }): Observable<Blob> {
    return this.withAuthHeaders().pipe(
      switchMap(headers => {
        let params = new HttpParams();
        if (filters.estado)       params = params.set('estado', filters.estado);
        if (filters.fecha_desde)  params = params.set('fecha_desde', filters.fecha_desde);
        if (filters.fecha_hasta)  params = params.set('fecha_hasta', filters.fecha_hasta);
        if (filters.grupo_pago)   params = params.set('grupo_pago', filters.grupo_pago);
        if (filters.año_gravable) params = params.set('año_gravable', String(filters.año_gravable));

        return this.http.get(
          `${this.cleanBase}${this.apiPrefix}/solicitudes/exportar-excel`,
          { headers, params, responseType: 'blob' }
        );
      }),
      catchError(error => {
        console.error('Error exportando Excel de utilidades:', error);
        return throwError(() => error);
      })
    );
  }


  // ======================================================
  // 11. POST /sincronizar/bidireccional
  // ======================================================
  sincronizarBidireccional(): Observable<ISincronizarBidireccionalResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.post<ISincronizarBidireccionalResponse>(
          `${this.cleanBase}${this.apiPrefix}/sincronizar/bidireccional`,
          {},
          { headers }
        )
      ),
      catchError(error => {
        console.error('Error sincronización bidireccional:', error);
        return throwError(() => error);
      })
    );
  }

  // ======================================================
  // 12. POST /sincronizar/firestore-to-postgres
  // ======================================================
  // v2 (2026-09-10): la sync es ASYNC en backend — retorna {migration_id}
  // inmediatamente y corre en background (antes: 504 al tardar >300 s).
  // El polling del progreso se hace con el store ya existente.
  sincronizarFirestoreAPostgres(): Observable<ISincronizarUnidireccionalResponse | any> {
      return this.withAuthHeaders().pipe(
        switchMap(headers =>
          this.http.post<any>(
            `${this.cleanBase}${this.apiPrefix}/sincronizar/firestore-to-postgres`,
            {},
            { headers }
          )
        ),
        catchError(error => {
          console.error('Error sincronizando Firestore→PostgreSQL:', error);
          return throwError(() => error);
        })
      );
  }

  // ======================================================
  // 13. POST /sincronizar/postgres-to-firestore
  // ======================================================
  sincronizarPostgresAFirestore(): Observable<ISincronizarUnidireccionalResponse> {
    return this.withAuthHeaders().pipe(
      switchMap(headers =>
        this.http.post<ISincronizarUnidireccionalResponse>(
          `${this.cleanBase}${this.apiPrefix}/sincronizar/postgres-to-firestore`,
          {},
          { headers }
        )
      ),
      catchError(error => {
        console.error('Error sincronizando PostgreSQL→Firestore:', error);
        return throwError(() => error);
      })
    );
  }
}


